import React from 'react';
import { useRM } from './useDeal';
import Skeleton from '@mui/material/Skeleton';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CallIcon from '@mui/icons-material/Call';
import Avatar from '@mui/material/Avatar';
import { RMType } from './types';
import { useAnalytics } from '../../hooks/useAnalytics';
import { DEAL_EVENTS } from './constants';

const ContactButtons = ({
  rm,
  track,
}: {
  rm: RMType;
  track: (event: { eventName: string; eventProperties?: any }) => void;
}) => {
  const handleEmailClick = () => {
    track({ eventName: DEAL_EVENTS.Rm_Reachout, eventProperties: { method: 'email' } });
    window.open(`mailto:${rm.email}`, '_blank');
  };

  const handleWhatsAppClick = () => {
    track({ eventName: DEAL_EVENTS.Rm_Reachout, eventProperties: { method: 'whatsapp' } });
    window.open(`https://wa.me/${rm.phone.replace('+', '')}`, '_blank');
  };

  const handleCallClick = () => {
    track({ eventName: DEAL_EVENTS.Rm_Reachout, eventProperties: { method: 'call' } });
    window.open(`tel:${rm.phone}`, '_blank');
  };

  const buttonClass = `
    bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.04)_132.14%),#FFFFFF]
    shadow-[0px_1.1px_1.8px_-0.3px_rgba(0,0,0,0.1)]
    rounded-lg border border-gray-300
    flex items-center justify-center gap-2 py-2 px-4 hover:bg-slate-50 transition-colors
    min-w-0 flex-1
  `;

  const iconClass = 'text-slate-600 w-4 h-4 flex-shrink-0';
  const textClass = 'text-sm font-medium text-slate-600 truncate';

  return (
    <div className="flex gap-3 w-full">
      <button onClick={handleEmailClick} className={buttonClass}>
        <EmailIcon className={iconClass} fontSize="small" />
        <span className={textClass}>Email</span>
      </button>
      <button onClick={handleWhatsAppClick} className={buttonClass}>
        <WhatsAppIcon className={iconClass} fontSize="small" />
        <span className={textClass}>WhatsApp</span>
      </button>
      <button onClick={handleCallClick} className={buttonClass}>
        <CallIcon className={iconClass} fontSize="small" />
        <span className={textClass}>Call</span>
      </button>
    </div>
  );
};

const RMSection = ({
  rm,
  role,
  track,
}: {
  rm: RMType;
  role: string;
  track: (event: { eventName: string; eventProperties?: any }) => void;
}) => {
  return (
    <div className="p-4 sm:p-5">
      <div className="flex flex-col items-start gap-4">
        <Avatar src={rm.imageLink || undefined} alt={rm.name} sx={{ width: 48, height: 48 }}>
          {rm.name?.charAt(0)}
        </Avatar>
        <div>
          <h3 className="text-base font-semibold text-slate-900">{rm.name}</h3>
          <p className="text-sm text-slate-500">{role}</p>
        </div>
      </div>
      <div className="mt-4">
        <ContactButtons rm={rm} track={track} />
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div className="p-4 sm:p-5">
    <div className="flex items-center gap-4">
      <Skeleton variant="circular" width={48} height={48} />
      <div className="flex-1">
        <Skeleton width={120} height={24} />
        <Skeleton width={80} height={20} />
      </div>
    </div>
    <div className="mt-4">
      <Skeleton width="100%" height={40} />
    </div>
  </div>
);

const RMCard = () => {
  const { data, isLoading } = useRM();
  const rms = data?.data;
  const { track } = useAnalytics();

  if (isLoading) {
    return (
      <div className="border bg-white border-zinc-200 rounded-lg divide-y">
        <LoadingSkeleton />
      </div>
    );
  }

  if (!rms) return null;

  return (
    <div className="border bg-white border-zinc-200 rounded-lg divide-y">
      {rms.investorSuccessRM && (
        <RMSection
          rm={rms.investorSuccessRM}
          role="Your dedicated RM assigned for all your queries and doubts."
          track={track}
        />
      )}
      {/* {rms.channelSalesRM && <RMSection rm={rms.channelSalesRM} role="Channel Sales Manager" />} */}
    </div>
  );
};

export default RMCard;
